<script setup>
  import { computed } from 'vue';

  const props = defineProps({
    name: {
      type: String,
      default: '',
    },
    modelValue: [Boolean, Array],
    value: [Boolean, Object, Number],
    type: {
      type: String,
      default: 'single',
      validator: (value) => {
        return ['single', 'all'].includes(value);
      },
    },
    checked: Boolean,
    disabled: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: 'purple'
    },
    onChange: {
      type: Function,
      default: null
    },
    id: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: 'md'
    },
    halfMarked: {
      type: Boolean,
      default: false,
    },
    multiple: Boolean,
    // The visible caption lives in the caller, so it names the box here.
    ariaLabel: String
  });
  const emit = defineEmits(['update:modelValue']);
  // Bound with v-model for its checked matching only; changes leave through
  // handleInput's emit, so the setter never writes the prop.
  const checkedModel = computed({ get: () => props.modelValue, set: () => {} });

  const handleInput = () => {
    if (props.multiple) {
      const value = props.modelValue || [];
      const index = value.indexOf(props.value);
      if (index === -1) {
        value.push(props.value);
      } else {
        value.splice(index, 1);
      }
      emit('update:modelValue', value);
    } else {
      emit('update:modelValue', !props.modelValue);
    }
  };
</script>

<template>
  <div class="pge-checkbox-main">
    <label :aria-label="props.ariaLabel || undefined">
      <!-- One input per kind of model, not per checked state: swapping inputs
           when a Boolean model toggles would drop keyboard focus. Array models
           keep v-model's element-wise matching. -->
      <input 
        v-if="Array.isArray(props.modelValue)"
        type="checkbox" 
        v-model="checkedModel"
        :value="props.value"
        :id="props.id" 
        :aria-label="props.ariaLabel || undefined"
        :class="props.halfMarked ? 'half-marked' : ''"
        :disabled="props.disabled" 
        :name="props.name"
        @change="props.onChange" 
        @input="handleInput"
      />
      <input 
        v-else
        type="checkbox" 
        :class="props.halfMarked ? 'half-marked' : ''"
        :id="props.id" 
        :aria-label="props.ariaLabel || undefined"
        :disabled="props.disabled" 
        :name="props.name"
        :checked="Boolean(props.modelValue) || props.checked"
        @change="props.onChange" 
        @input="handleInput"
      />
      <div
        :class="[
          `pge-checkbox pge-checkbox-${props.variant}`,
          props.type === 'all' ? 'all' : '',
          props.size
        ]"
      >
        <div class="state-layer">
          <div class="pge-container">
            <span class="icon"></span>
          </div>
          <div class="ripple"></div>
        </div>
      </div>
    </label>
  </div>
</template>

<style lang="scss" scoped>
.pge-checkbox-main {
  // Visually hidden, not display: none, so the real checkbox stays in the
  // tab order and screen readers can reach it. Absolute with no offsets keeps
  // it where it sits, so focusing it never scrolls.
  & input[type='checkbox'] {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: 0;
    opacity: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
  }

  & .pge-checkbox {
    display: inline-flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    & .state-layer {
      display: flex;
      padding: 10px;
      justify-content: center;
      align-items: center;
      border-radius: 100px;
      position: relative;
      overflow: hidden;

      & .pge-container {
        width: 18px;
        height: 18px;
        border-radius: 2px;
        cursor: pointer;
        border: 2px solid transparent;

        & .icon {
          width: 24px;
          height: 24px;
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
        }
      }
    }

    &.sm {
      & .state-layer {
        padding: 11px;

        & .pge-container {
          width: 16px;
          height: 16px;
        }
      }
    }
  }
}

input[type='checkbox']:checked+.pge-checkbox {
  .icon::before {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M10 16.4L6 12.4L7.4 11L10 13.6L16.6 7L18 8.4L10 16.4Z" fill="white"/></svg>');
  }
}

input[type='checkbox']:checked+.pge-checkbox .pge-container {
  background-color: #5E2F78;

  & .icon::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}

input[type='checkbox']:checked:disabled+.pge-checkbox {
  opacity: 0.38;

  & .state-layer {
    .pge-container {
      border-radius: 2px;
      background-color: #1D1B18;

      & .icon {
        fill: #FEF7FF;
      }
    }
  }
}

input[type='checkbox']:checked:not(:disabled):hover+.pge-checkbox {
  .state-layer {
    background-color: rgba(94, 47, 120, 0.08) !important;
  }
}

input[type='checkbox']:checked:not(:disabled):focus-visible+.pge-checkbox {
  .state-layer {
    background-color: rgba(94, 47, 120, 0.12) !important;
  }
}

input[type='checkbox']:checked:not(:disabled):active+.pge-checkbox {
  .state-layer {
    background-color: rgba(94, 47, 120, 0.12) !important;

    & .pge-container {
      border-radius: 2px;
      background-color: #5E2F78;
    }

    & .ripple {
      position: absolute;
      width: 40px;
      height: 35px;
      bottom: 0;
      fill: #1D1B20;
      opacity: 0.2;
      animation: rippleAnimation 200ms cubic-bezier(0.2, 0, 0, 1) forwards;
    }

    & .ripple::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-image: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="40" height="35" viewBox="0 0 40 35" fill="none"%3E%3Cpath opacity="0.2" d="M40 3.90486V31.2162C40 33.3059 38.3418 35 36.2963 35H-10C-10 15.67 5.33839 0 24.2593 0C29.9342 0 35.2868 1.40963 40 3.90486Z" fill="%231D1B20"%3E%3C/path%3E%3C/svg%3E');
    }
  }
}

input[type='checkbox']:not(:checked)+.pge-checkbox {
  .state-layer {
    .pge-container {
      border: 2px solid #4B444D;
    }
  }
}

input[type='checkbox']:not(:checked):disabled+.pge-checkbox {
  opacity: 0.38;

  & .state-layer {
    .pge-container {
      border-radius: 2px;
      border: 2px solid #1D1B20;

      & .icon {
        fill: #FEF7FF;
      }
    }
  }
}

input[type='checkbox']:not(:checked):not(:disabled):hover+.pge-checkbox {
  .state-layer {
    background-color: rgba(29, 27, 32, 0.08);

    & .pge-container {
      border-radius: 2px;
      border: 2px solid #1D1B20;
    }
  }
}

input[type='checkbox']:not(:checked):not(:disabled):focus-visible+.pge-checkbox {
  .state-layer {
    border-radius: 100px;
    background-color: rgba(29, 27, 32, 0.12);

    & .pge-container {
      border-radius: 2px;
      border: 2px solid #1D1B20;
    }
  }
}

input[type='checkbox']:not(:checked):not(:disabled):active+.pge-checkbox {
  .state-layer {
    border-radius: 100px;
    background-color: rgba(29, 27, 32, 0.12);

    & .pge-container {
      border-radius: 2px;
      border: 2px solid #1D1B20;
    }

    & .ripple {
      position: absolute;
      width: 40px;
      height: 35px;
      bottom: 0;
      animation: rippleAnimation 200ms cubic-bezier(0.2, 0, 0, 1) forwards;
      fill: #5E2F78;
      opacity: 0.2;
    }

    & .ripple::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-image: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="40" height="35" viewBox="0 0 40 35" fill="none"%3E%3Cpath opacity="0.2" d="M40 3.90486V31.2162C40 33.3059 38.3418 35 36.2963 35H-10C-10 15.67 5.33839 0 24.2593 0C29.9342 0 35.2868 1.40963 40 3.90486Z" fill="%231D1B20"%3E%3C/path%3E%3C/svg%3E');
    }
  }
}

input[type='checkbox'].half-marked+.pge-checkbox .icon::before {
  background-image: url("data:image/png;base64,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")
}

input[type='checkbox'].half-marked+.pge-checkbox .pge-container {
  background-color: var(--XL-Purple-100);

  & .icon::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}

input[type='checkbox'].half-marked:disabled+.pge-checkbox {
  opacity: 0.38;

  & .state-layer {
    .pge-container {
      border-radius: 2px;
      background-color: var(--On-Surface-Gray);

      & .icon {
        fill: #FEF7FF;
      }
    }
  }
}

input[type='checkbox'].half-marked:not(:disabled):hover+.pge-checkbox {
  .state-layer {
    background-color: var(--XL-Purple-08) !important;
  }
}

input[type='checkbox'].half-marked:not(:disabled):focus-visible+.pge-checkbox {
  .state-layer {
    background-color: var(--XL-Purple-12) !important;
  }
}

input[type='checkbox'].half-marked:not(:disabled):active+.pge-checkbox {
  .state-layer {
    background-color: var(--XL-Purple-12) !important;

    & .pge-container {
      border-radius: 2px;
      background-color: var(--XL-Purple-100);
    }

    & .ripple {
      position: absolute;
      width: 40px;
      height: 35px;
      bottom: 0;
      fill: var(--On-Surface-Gray);
      opacity: 0.2;
      animation: rippleAnimation 200ms cubic-bezier(0.2, 0, 0, 1) forwards;
    }

    & .ripple::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-image: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="40" height="35" viewBox="0 0 40 35" fill="none"%3E%3Cpath opacity="0.2" d="M40 3.90486V31.2162C40 33.3059 38.3418 35 36.2963 35H-10C-10 15.67 5.33839 0 24.2593 0C29.9342 0 35.2868 1.40963 40 3.90486Z" fill="%231D1B20"%3E%3C/path%3E%3C/svg%3E');
    }
  }
}

@keyframes rippleAnimation {
  0% {
    transform: scale(0);
    opacity: 0.5;
  }

  100% {
    transform: scale(2);
    opacity: 0;
  }
}
</style>
